import axios from "axios";

import {
  TwoFactorBackupCodesDto,
  TwoFactorSetupDto,
  TwoFactorStatusDto,
  TwoFactorStepUpSentDto,
  UserTwoFactorMethod,
} from "@/types/auth";

export type TwoFactorStatus = TwoFactorStatusDto;
export type TwoFactorSetup = TwoFactorSetupDto;
export type TwoFactorMethod = UserTwoFactorMethod;

/** Either proof the confirmation dialog can submit: a TOTP/e-mail code, or the account password. */
export type ConfirmationProof = { code: string } | { password: string };

/** Mirrors `TwoFactorException`/`TwoFactorErrorDto.code` on the backend: a stable identifier the
 * screen switches on, not a message for users. `TOO_MANY_ATTEMPTS` never appears in a 400 body -
 * it is synthesized here from a 429 (the confirmation throttle, or the step-up send budget). */
export type TwoFactorErrorCode =
  "INVALID_CODE" | "INVALID_PASSWORD" | "ALREADY_ACTIVE" | "NOT_ENROLLED" | "TOO_MANY_ATTEMPTS";

export class TwoFactorError extends Error {
  readonly code: TwoFactorErrorCode;
  readonly retryAfterSeconds?: number;

  constructor(code: TwoFactorErrorCode, message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = "TwoFactorError";
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Converts a failed request into a `TwoFactorError`. The confirmation and step-up-send throttles
 * answer 429 with a `Retry-After` header and no usable body, so that is handled as its own case
 * rather than falling through to the generic `{code, message}` parsing below.
 */
const toTwoFactorError = (e: unknown, fallbackCode: TwoFactorErrorCode): TwoFactorError => {
  if (axios.isAxiosError(e)) {
    if (e.response?.status === 429) {
      const retryAfterSeconds = Number(e.response.headers["retry-after"]);
      return new TwoFactorError(
        "TOO_MANY_ATTEMPTS",
        "Too many attempts. Please wait before trying again.",
        Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : undefined,
      );
    }
    const data = e.response?.data as { code?: TwoFactorErrorCode; message?: string } | undefined;
    if (data?.code) {
      return new TwoFactorError(data.code, data.message ?? data.code);
    }
  }
  return new TwoFactorError(fallbackCode, "Something went wrong. Please try again.");
};

export const fetchTwoFactorStatus = async (): Promise<TwoFactorStatus> =>
  (await axios.get<TwoFactorStatus>("/api/self/2fa")).data;

export const startTwoFactorSetup = async (
  method: TwoFactorMethod = "TOTP",
): Promise<TwoFactorSetup> => {
  try {
    return (await axios.post<TwoFactorSetup>("/api/self/2fa/setup", { method })).data;
  } catch (e) {
    throw toTwoFactorError(e, "NOT_ENROLLED");
  }
};

export const activateTwoFactor = async (code: string): Promise<string[]> => {
  try {
    return (await axios.post<TwoFactorBackupCodesDto>("/api/self/2fa/activate", { code })).data
      .codes;
  } catch (e) {
    throw toTwoFactorError(e, "INVALID_CODE");
  }
};

const fallbackFor = (proof: ConfirmationProof): TwoFactorErrorCode =>
  "password" in proof ? "INVALID_PASSWORD" : "INVALID_CODE";

export const regenerateBackupCodes = async (proof: ConfirmationProof): Promise<string[]> => {
  try {
    return (await axios.post<TwoFactorBackupCodesDto>("/api/self/2fa/backup-codes", proof)).data
      .codes;
  } catch (e) {
    throw toTwoFactorError(e, fallbackFor(proof));
  }
};

export const disableTwoFactor = async (proof: ConfirmationProof): Promise<void> => {
  try {
    await axios.post("/api/self/2fa/disable", proof);
  } catch (e) {
    throw toTwoFactorError(e, fallbackFor(proof));
  }
};

/** A code for the confirmation dialog; only e-mail accounts have one to send. */
export const sendStepUpCode = async (): Promise<TwoFactorStepUpSentDto> => {
  try {
    return (await axios.post<TwoFactorStepUpSentDto>("/api/self/2fa/step-up/send")).data;
  } catch (e) {
    throw toTwoFactorError(e, "NOT_ENROLLED");
  }
};
